import api from './api';
import type { CurrencyCode } from '@/constants/currency';

export interface PricingSize {
  label: string;
  widthMm: number;
  heightMm: number;
}

export interface PaperPricing {
  id: string;
  name: string;
  brand: string;
  category: string;
  description: string | null;
  weightGsm: number;
  widthIn: number;
  lengthIn: number;
  isRoll: boolean;
  priceBySizeLabel: Record<string, number>;
}

export interface PrinterPricingSummary {
  id: string;
  name: string;
  brand: string;
  model: string;
}

export interface PricingSummary {
  discountPercentage: number;
  sizes: PricingSize[];
  printer: PrinterPricingSummary;
  paperTypes: PaperPricing[];
  currency: CurrencyCode;
  exchangeRate?: {
    baseCurrency: string;
    quoteCurrency: string;
    rate: number;
    effectiveDate: string;
    source: string;
  } | null;
}

export const getPricingSummary = async (currency?: CurrencyCode): Promise<PricingSummary> => {
  const response = await api.get<PricingSummary>('/pricing', {
    params: currency ? { currency } : undefined,
  });
  return response.data;
};

export interface PricingCalculationRowInput {
  label: string;
  widthInches: number | null;
  heightInches: number | null;
  discountPercentage: number;
}

export interface PricingCalculationResult {
  paperTypePrices: Record<string, (number | null)[]>;
}

export interface OrderItemPricingRequest {
  uploadedFileId: string;
  widthInches?: number | null;
  heightInches?: number | null;
  galleryWrapInches?: number | null;
  paperTypeId?: string | null;
  printerId?: string | null;
}

export interface OrderItemPricingResponse {
  price: {
    value: number;
    currency: string;
  };
  resolvedPaperTypeId: string;
  resolvedPrinterId: string;
  dimensions: {
    widthInches: number;
    heightInches: number;
  };
}

interface CalculatePricingOptions {
  signal?: AbortSignal;
  currency?: CurrencyCode;
}

export const calculatePricing = async (
  rows: PricingCalculationRowInput[],
  options?: CalculatePricingOptions,
): Promise<PricingCalculationResult> => {
  const response = await api.post<PricingCalculationResult>(
    '/pricing/calculate',
    { rows },
    {
      params: options?.currency ? { currency: options.currency } : undefined,
      signal: options?.signal,
    },
  );
  return response.data;
};

export const calculateOrderItemPricing = async (
  payload: OrderItemPricingRequest,
  options?: { signal?: AbortSignal }
): Promise<OrderItemPricingResponse> => {
  const response = await api.post<OrderItemPricingResponse>(
    '/pricing/item',
    payload,
    { signal: options?.signal }
  );
  return response.data;
};
